/**
 * Admin settings panel component.
 */

import { useCallback, useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { AlertTriangle, Loader2 } from "lucide-react";
import { systemSettingsService } from "@/services/systemSettings.service";
import type {
  SearchEngineMode,
  SearchEngineModeSwitchResponse,
  SystemSettingResponse,
} from "@/types/systemSettings.types";
// @ts-ignore
import { toast } from "sonner";

const SEARCH_ENGINE_MODE_KEY = "searchEngineMode";
const READ_ONLY_SETTING_KEYS = new Set(["webkurator.categories.catalog.hash"]);
const SEARCH_ENGINE_MODES: SearchEngineMode[] = [
  "POSTGRES",
  "SHADOW",
  "OPENSEARCH",
];

export function SettingsPanel() {
  const { t } = useApp();
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const borderStyle = {
    borderColor: "color-mix(in srgb, var(--foreground) 12%, var(--background))",
  } as const;
  const warningStyle = {
    backgroundColor:
      "color-mix(in srgb, var(--accent) 16%, var(--background))",
    borderColor:
      "color-mix(in srgb, var(--accent) 26%, var(--background))",
    color: "color-mix(in srgb, var(--foreground) 82%, var(--surface))",
  } as const;
  const [settings, setSettings] = useState<SystemSettingResponse[]>([]);
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [savingKeys, setSavingKeys] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchModeStatus, setSearchModeStatus] =
    useState<SearchEngineModeSwitchResponse | null>(null);
  const [pendingSearchMode, setPendingSearchMode] =
    useState<SearchEngineMode | null>(null);
  const [searchModeConfirmOpen, setSearchModeConfirmOpen] = useState(false);
  const [switchingSearchMode, setSwitchingSearchMode] = useState(false);

  const loadSearchModeStatus = useCallback(async () => {
    try {
      const status = await systemSettingsService.getSearchEngineModeStatus();
      setSearchModeStatus(status);
      setDraftValues((prev) => ({
        ...prev,
        [SEARCH_ENGINE_MODE_KEY]: String(status.currentMode),
      }));
      setSettings((prev) =>
        prev.map((setting) =>
          setting.key === SEARCH_ENGINE_MODE_KEY
            ? { ...setting, value: String(status.currentMode) }
            : setting
        )
      );
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? t("common.genericError"));
    }
  }, [t]);

  useEffect(() => {
    let cancelled = false;

    const loadSettings = async () => {
      try {
        const response = await systemSettingsService.getSystemSettings();
        if (!cancelled) {
          setSettings(response);
          setDraftValues(
            response.reduce<Record<string, string>>((acc, setting) => {
              acc[setting.key] = setting.value;
              return acc;
            }, {})
          );
        }
      } catch (error: any) {
        if (!cancelled) {
          setSettings([]);
        }
        toast.error(error.response?.data?.message ?? t("common.genericError"));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    loadSearchModeStatus();
  }, [loadSearchModeStatus]);

  useEffect(() => {
    if (
      !searchModeStatus?.switchPending &&
      !searchModeStatus?.rebuildRunning
    ) {
      return;
    }
    const timer = window.setInterval(() => {
      loadSearchModeStatus();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [
    loadSearchModeStatus,
    searchModeStatus?.rebuildRunning,
    searchModeStatus?.switchPending,
  ]);

  const resolveTranslation = (key: string, fallback = "") => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const setSavingState = (key: string, value: boolean) => {
    setSavingKeys((prev) => ({ ...prev, [key]: value }));
  };

  const updateSettingValue = async (
    setting: SystemSettingResponse,
    value: string,
    options: { revertOnError?: boolean } = {}
  ) => {
    if (savingKeys[setting.key]) {
      return;
    }
    setSavingState(setting.key, true);
    try {
      const updated = await systemSettingsService.updateSystemSetting(
        setting.key,
        {
          type: setting.type,
          value,
        }
      );
      setSettings((prev) =>
        prev.map((item) => (item.key === updated.key ? updated : item))
      );
      setDraftValues((prev) => ({ ...prev, [updated.key]: updated.value }));
      toast.success(t("common.saved"));
    } catch (error: any) {
      if (options.revertOnError) {
        setDraftValues((prev) => ({ ...prev, [setting.key]: setting.value }));
      }
      toast.error(error.response?.data?.message ?? t("common.genericError"));
    } finally {
      setSavingState(setting.key, false);
    }
  };

  const updateSearchModeValue = (
    status: SearchEngineModeSwitchResponse,
    requestedMode: SearchEngineMode
  ) => {
    setSearchModeStatus(status);
    const nextValue = String(
      status.applied ? status.currentMode : status.currentMode || requestedMode
    );
    setDraftValues((prev) => ({
      ...prev,
      [SEARCH_ENGINE_MODE_KEY]: nextValue,
    }));
    setSettings((prev) =>
      prev.map((setting) =>
        setting.key === SEARCH_ENGINE_MODE_KEY
          ? { ...setting, value: nextValue }
          : setting
      )
    );
  };

  const requestSearchModeSwitch = async (
    requestedMode: SearchEngineMode,
    confirmBuildIndex: boolean
  ) => {
    try {
      setSwitchingSearchMode(true);
      const response = await systemSettingsService.switchSearchEngineMode({
        mode: requestedMode,
        confirmBuildIndex,
      });
      updateSearchModeValue(response, requestedMode);
      if (response.confirmationRequired) {
        setPendingSearchMode(requestedMode);
        setSearchModeConfirmOpen(true);
        return;
      }
      if (response.applied) {
        toast.success(t("admin.searchEngineModeApplied"));
        return;
      }
      if (response.accepted) {
        toast.success(t("admin.searchEngineModeRebuildStarted"));
        return;
      }
      toast.error(response.message || t("common.genericError"));
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? t("common.genericError"));
    } finally {
      setSwitchingSearchMode(false);
    }
  };

  const handleSearchModeChange = (nextMode: string) => {
    const requestedMode = SEARCH_ENGINE_MODES.find((mode) => mode === nextMode);
    if (!requestedMode) {
      return;
    }
    const currentMode = String(searchModeStatus?.currentMode ?? "");
    if (requestedMode === currentMode) {
      return;
    }
    requestSearchModeSwitch(requestedMode, false);
  };

  const confirmSearchModeRebuild = async () => {
    if (!pendingSearchMode) {
      return;
    }
    setSearchModeConfirmOpen(false);
    await requestSearchModeSwitch(pendingSearchMode, true);
    setPendingSearchMode(null);
  };

  return (
    <>
      <AlertDialog
        open={searchModeConfirmOpen}
        onOpenChange={setSearchModeConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("admin.searchEngineModeConfirmTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin.searchEngineModeConfirmDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={switchingSearchMode}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmSearchModeRebuild}
              disabled={switchingSearchMode}
            >
              {switchingSearchMode ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t("admin.searchEngineModeConfirmAction")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="grid gap-6">
      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="card-title-text" style={primaryTextStyle}>
            {t("admin.systemSettings")}
          </CardTitle>
          <CardDescription
            className="card-description-text"
            style={secondaryTextStyle}
          >
            {t("admin.systemSettingsDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm" style={secondaryTextStyle}>
              {t("common.loading")}
            </p>
          ) : settings.length === 0 ? (
            <p className="text-sm" style={secondaryTextStyle}>
              {t("admin.systemSettingsEmpty")}
            </p>
          ) : (
            <div className="space-y-4">
              {settings.map((setting) => {
                const label = resolveTranslation(
                  `admin.systemSettingsItems.${setting.key}.label`,
                  setting.key
                );
                const description = resolveTranslation(
                  `admin.systemSettingsItems.${setting.key}.description`
                );
                const warning = resolveTranslation(
                  `admin.systemSettingsItems.${setting.key}.warning`
                );
                const draftValue = draftValues[setting.key];
                const resolvedValue = draftValue ?? setting.value;
                const isBoolean = setting.type === "BOOLEAN";
                const isNumber = setting.type === "NUMBER";
                const isText = setting.type === "TEXT";
                const isSearchEngineMode =
                  setting.key === SEARCH_ENGINE_MODE_KEY;
                const resolvedBooleanValue = resolvedValue === "true";
                const isSaving = savingKeys[setting.key];
                const isReadOnly = READ_ONLY_SETTING_KEYS.has(setting.key);
                const searchModeControlDisabled =
                  switchingSearchMode ||
                  Boolean(searchModeStatus?.switchPending) ||
                  Boolean(searchModeStatus?.rebuildRunning);
                const handleBooleanChange = (nextValue: boolean) => {
                  if (isSaving || isReadOnly) {
                    return;
                  }
                  const nextValueString = String(nextValue);
                  if (nextValueString === resolvedValue) {
                    return;
                  }
                  setDraftValues((prev) => ({
                    ...prev,
                    [setting.key]: nextValueString,
                  }));
                  updateSettingValue(setting, nextValueString, {
                    revertOnError: true,
                  });
                };

                return (
                  <div
                    key={setting.key}
                    className="space-y-3 rounded-lg border p-4"
                    style={borderStyle}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1">
                        <p style={primaryTextStyle}>{label}</p>
                        {description && (
                          <p className="text-sm" style={secondaryTextStyle}>
                            {description}
                          </p>
                        )}
                      </div>
                      {isSearchEngineMode ? (
                        <div className="ml-auto min-w-[220px]">
                          <Select
                            value={String(resolvedValue || "POSTGRES")}
                            onValueChange={handleSearchModeChange}
                            disabled={searchModeControlDisabled}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {SEARCH_ENGINE_MODES.map((mode) => (
                                <SelectItem key={mode} value={mode}>
                                  {mode}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : isBoolean ? (
                        <div
                          role="group"
                          aria-label={label}
                          className="ml-auto inline-flex items-center gap-2"
                        >
                          <Button
                            type="button"
                            size="sm"
                            variant={
                              resolvedBooleanValue ? "outline" : "secondary"
                            }
                            onClick={() => handleBooleanChange(false)}
                            disabled={isSaving}
                            className="min-w-[110px] cursor-pointer justify-center"
                            aria-pressed={!resolvedBooleanValue}
                          >
                            {t("admin.disabled")}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={
                              resolvedBooleanValue ? "default" : "outline"
                            }
                            onClick={() => handleBooleanChange(true)}
                            disabled={isSaving}
                            className={`min-w-[110px] cursor-pointer justify-center${
                              resolvedBooleanValue ? " btn-primary" : ""
                            }`}
                            aria-pressed={resolvedBooleanValue}
                          >
                            {t("admin.enabled")}
                          </Button>
                        </div>
                      ) : isReadOnly ? (
                        <div className="ml-auto">
                          <Input
                            type="text"
                            value={resolvedValue}
                            readOnly
                            className="max-w-[340px] font-mono text-xs"
                          />
                        </div>
                      ) : (
                        <div className="ml-auto flex items-center gap-2">
                          <Input
                            type={isNumber ? "number" : "text"}
                            value={resolvedValue}
                            onChange={(event) =>
                              setDraftValues((prev) => ({
                                ...prev,
                                [setting.key]: event.target.value,
                              }))
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                updateSettingValue(setting, resolvedValue);
                              }
                            }}
                            disabled={savingKeys[setting.key] || isReadOnly}
                            className="max-w-[220px]"
                          />
                          <Button
                            variant="outline"
                            onClick={() =>
                              updateSettingValue(setting, resolvedValue)
                            }
                            disabled={savingKeys[setting.key] || isReadOnly}
                          >
                            {t("common.save")}
                          </Button>
                        </div>
                      )}
                    </div>
                    {warning && (
                      <div
                        className="flex items-start gap-2 rounded-lg border p-3 text-sm"
                        style={warningStyle}
                      >
                        <AlertTriangle className="mt-0.5 h-4 w-4" style={iconStyle} />
                        <span>{warning}</span>
                      </div>
                    )}
                    {isSearchEngineMode &&
                    (searchModeStatus?.switchPending ||
                      searchModeStatus?.rebuildRunning) ? (
                      <div
                        className="flex items-start gap-2 rounded-lg border p-3 text-sm"
                        style={warningStyle}
                      >
                        <Loader2 className="mt-0.5 h-4 w-4 animate-spin" style={iconStyle} />
                        <span>{t("admin.searchEngineModePending")}</span>
                      </div>
                    ) : null}
                    {!isBoolean && !isNumber && !isText && (
                      <p className="text-sm" style={secondaryTextStyle}>
                        {t("admin.systemSettingsUnsupportedType")}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </>
  );
}
